import React, { useState } from 'react';
import CommonButton from '../../../../Components/CommonButton';
import CommonLoader from '../../../../Components/CommonLoader';
import CommonModal from '../../../../Components/CommonModal';
import Section from './Section';
import AddOnCard from '../../../../Dashboard/AddOns/AddOnCard';
import { risk_portal_save_quantification, risk_portal_monte_carlo } from '../../../../services';
import { Body, fontSize } from '../../../../Constants/Fonts';
import { extractItem, money } from './constants';

// QuantificationSection — FAIR financial exposure (Asset Value / Exposure Factor
// / SLE / ARO / ALE) with an INLINE edit form + a Monte Carlo simulation.
export default function QuantificationSection({ riskId, quantification, submitStage, canSubmit, stageBtnText, busy, notify }) {
    const [mcBusy, setMcBusy] = useState(false);
    const [mc, setMc] = useState(null);
    // Live form values (from the edit form's onValuesChange) so the FAIR cards
    // recompute as the user types — no need to save first. Null until first edit,
    // where we fall back to the saved quantification.
    const [live, setLive] = useState(null);

    const q = quantification || {};
    // Source the cards from live edits when present, else the saved values. SLE
    // and ALE are derived (Asset×EF, SLE×ARO); use the server-computed figures
    // only for the saved (non-live) view when they exist.
    const src = live || q;
    const av = Number(src.asset_value || 0);
    const ef = Number(src.exposure_factor || 0);
    const aro = Number(src.annual_rate_of_occurrence || 0);
    const sle = live ? av * ef : (q.single_loss_expectancy ?? av * ef);
    const ale = live ? sle * aro : (q.annualized_loss_expectancy ?? sle * aro);
    const mcResult = mc || q.monte_carlo_results;

    const saveQuantification = (data) => risk_portal_save_quantification(riskId, {
        asset_value: Number(data.asset_value) || 0,
        exposure_factor: Number(data.exposure_factor) || 0,
        annual_rate_of_occurrence: Number(data.annual_rate_of_occurrence) || 0,
        treatment_cost: data.treatment_cost === '' ? null : Number(data.treatment_cost),
        best_case_loss: Number(data.best_case_loss) || 0,
        most_likely_loss: Number(data.most_likely_loss) || 0,
        worst_case_loss: Number(data.worst_case_loss) || 0,
    });

    // Monte Carlo is a standalone (non-advancing) action, so it keeps its own
    // local busy flag rather than the shared stage-progression one. The backend
    // simulates off the SAVED quantification, so persist the current form values
    // first — otherwise it rejects with "Set best/most_likely/worst case losses
    // first" even though the FAIR cards already show them.
    const runMonteCarlo = async () => {
        const source = live || q;
        if (!(Number(source.best_case_loss) > 0 && Number(source.most_likely_loss) > 0 && Number(source.worst_case_loss) > 0)) {
            notify('Enter Best, Most Likely and Worst Case losses first, then run the simulation.', 'warning');
            return;
        }
        setMcBusy(true);
        try {
            if (live) await saveQuantification(live);   // persist just-typed values so the simulation can read them
            const d = extractItem(await risk_portal_monte_carlo(riskId));
            if (d) { setMc(d); notify(`Simulation complete: ${d.iterations || '10K'} iterations`, 'success'); }
            else notify('Set best/most-likely/worst-case losses first.', 'warning');
        } catch (e) {
            notify(e?.response?.data?.error?.message || 'Simulation failed. Set best/most likely/worst case losses first.', 'error');
        } finally { setMcBusy(false); }
    };

    return (
        <Section title="Financial Quantification (FAIR)">
            <div className="rp1_fair_grid">
                <Metric label="Asset Value" value={money(src.asset_value)} />
                <Metric label="Exposure Factor" value={`${Math.round(ef * 100)}%`} />
                <Metric label="SLE" value={money(sle)} hint="Single Loss Expectancy" />
                <Metric label="ARO" value={aro ? `${aro}/yr` : 'x/yr'} />
                <Metric label="ALE" value={money(ale)} hint="Annual Loss Expectancy" danger />
            </div>
            <div style={{ marginTop: 16 }}>
                <CommonButton text="Run Monte Carlo (10K iterations)" onClick={runMonteCarlo} disable={mcBusy || busy} />
            </div>
            {mcBusy && <div style={{ marginTop: 12 }}><CommonLoader label="Simulating…" /></div>}
            {mcResult && (
                <div className="rp1_fair_grid" style={{ marginTop: 16 }}>
                    <Metric label="Mean ALE" value={money(mcResult.mean || mcResult.mean_ale)} />
                    <Metric label="P50" value={money(mcResult.p50 || mcResult.median)} />
                    <Metric label="P90" value={money(mcResult.p90)} />
                    <Metric label="P99" value={money(mcResult.p99)} />
                    <Metric label="Iterations" value={(mcResult.iterations || 10000).toLocaleString()} />
                </div>
            )}

            {/* FAIR quantification form — only for the active stage owner; the
                metrics above stay as a read-only view otherwise. Its single button
                saves then advances (this is the approval stage, so submitStage
                accepts the risk). */}
            {canSubmit && (
                <CommonModal
                    inline
                    title="Edit Financial Quantification"
                    subtitle="FAIR inputs — SLE, ARO and ALE are derived from these."
                    fields={QUANT_FIELDS.map((fld) => ({ ...fld, type: 'number', half: true }))}
                    defaultValues={{
                        asset_value: q.asset_value || '', exposure_factor: q.exposure_factor || '',
                        annual_rate_of_occurrence: q.annual_rate_of_occurrence || '', treatment_cost: q.treatment_cost || '',
                        best_case_loss: q.best_case_loss || '', most_likely_loss: q.most_likely_loss || '', worst_case_loss: q.worst_case_loss || '',
                    }}
                    onValuesChange={setLive}
                    onSubmit={(data) => submitStage(() => saveQuantification(data))}
                    buttons={[{ text: stageBtnText, variant: 'primary', type: 'submit', loading: busy, loadingText: 'Saving…' }]}
                />
            )}
        </Section>
    );
}

// FAIR quantification inputs — SLE, ARO and ALE derive from these.
const QUANT_FIELDS = [
    { name: 'asset_value', label: 'Asset Value ($)' },
    { name: 'exposure_factor', label: 'Exposure Factor (0–1)' },
    { name: 'annual_rate_of_occurrence', label: 'Annual Rate of Occurrence' },
    { name: 'treatment_cost', label: 'Treatment Cost ($)' },
    { name: 'best_case_loss', label: 'Best Case Loss ($)' },
    { name: 'most_likely_loss', label: 'Most Likely Loss ($)' },
    { name: 'worst_case_loss', label: 'Worst Case Loss ($)' },
];

function Metric({ label, value, hint, danger }) {
    return (
        <AddOnCard
            label={
                <Body as="span" style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Body as="span" variant={2} weight="medium" color="var(--color-text-muted)" style={{ fontSize: fontSize.sm, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</Body>
                    {hint && <Body as="span" variant={2} color="var(--color-text-muted)">{hint}</Body>}
                </Body>
            }
            // the metric value as the trailing action (passing `action` keeps
            // AddOnCard out of its default Enable/Active mode); red for ALE/danger
            action={<Body as="span" color={danger ? 'var(--color-danger)' : 'var(--color-text-primary)'} weight="bold" style={{ fontSize: 20 }}>{value}</Body>}
        />
    );
}
